import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  compose,
  withAccountHeader,
  withAuthHeader,
  withRetries,
  withConfiguredBaseUrl,
  withE2ERequestStubbing,
} from '../hooks/useService';
import urls from '../util/urls';
import { ServiceAuth } from '../hooks/useService/useService';
import cloneDeep from 'lodash/cloneDeep';
import flatten from 'lodash/flatten';
import { IAxiosRetryConfig } from 'axios-retry';

interface Options {
  axiosOptions?: AxiosRequestConfig;
  axiosRetryConfig?: IAxiosRetryConfig;
  [key: string]: any;
}

export type FhirSearchResourceReturnValue<TResource> = {
  _scroll_id?: string;
  hits: {
    hits: {
      _source: TResource;
    }[];
  };
};

export type BuildQuery = (pageIndex: number) => any;

/**
 * Pagination handler for FHIR search requests that return a simple response listing fhir resources.
 */
export class ResourceFhirSearchPaginationHandler<TResource>
  implements PaginationHandler<FhirSearchResourceReturnValue<TResource>>
{
  private _pageIndex: number;
  private _buildQuery: BuildQuery;
  private _maxResources: number;
  constructor(buildQuery: BuildQuery, maxResources: number) {
    this._maxResources = maxResources;
    this._pageIndex = 0;
    this._buildQuery = buildQuery;
  }
  buildNextQuery = () => {
    const query = this._buildQuery(this._pageIndex);
    this._pageIndex++;
    return query;
  };
  hasNextPage = (response: FhirSearchResourceReturnValue<TResource>) => {
    return response?.hits?.hits.length === this._maxResources;
  };
  mergeResponses = (
    response: FhirSearchResourceReturnValue<TResource>[],
  ): FhirSearchResourceReturnValue<TResource> => {
    const allHits = response.map(_ => _.hits.hits);
    const refResponse = cloneDeep(response[0]);
    refResponse.hits.hits = flatten(allHits);
    return refResponse;
  };

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  getQueryParams = (lastResponse: FhirSearchResourceReturnValue<TResource>) => {
    return lastResponse?._scroll_id
      ? { scroll: lastResponse?._scroll_id }
      : { scroll: true };
  };
}

export interface PaginationHandler<TResponse = any> {
  buildNextQuery: (lastResponse: TResponse, query: any) => any;
  hasNextPage: (response: TResponse, pageNumber: number) => boolean;
  mergeResponses: (response: TResponse[]) => TResponse;
  getQueryParams?: (lastResponse: TResponse | null) => any;
}

export default class FhirSearchService {
  getFhirSearchAPI: (auth: ServiceAuth) => AxiosInstance;
  constructor(_options?: Partial<Options>) {
    this.getFhirSearchAPI = (auth: ServiceAuth) =>
      compose(
        withE2ERequestStubbing,
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(_options?.axiosRetryConfig),
      )(axios.create());
  }

  private _paginatedRequest = async <TResponse>(
    paginationHandler: PaginationHandler,
    startQuery: any,
    makeRequest: (
      query: any,
      queryParams: any | null,
    ) => Promise<AxiosResponse<TResponse>>,
    baseParams?: any,
  ) => {
    if (paginationHandler) {
      let pageNumber = 0;
      const { buildNextQuery, hasNextPage, mergeResponses } = paginationHandler;
      let axiosResponse = {};
      const responses: any[] = [];
      const getLastResponse = () => responses[responses.length - 1] || null;
      do {
        const paginationParams =
          paginationHandler?.getQueryParams?.(getLastResponse()) || null;
        const queryParams = { ...baseParams, ...paginationParams };
        const paginatedQuery = getLastResponse()
          ? buildNextQuery(getLastResponse(), cloneDeep(startQuery))
          : startQuery;

        const pagedResponse = await makeRequest(paginatedQuery, queryParams);
        axiosResponse = pagedResponse;
        responses.push(pagedResponse.data);
      } while (hasNextPage(getLastResponse(), pageNumber++));

      const response = {
        ...axiosResponse,
        data: mergeResponses(responses),
      } as unknown as AxiosResponse<TResponse>;

      return response;
    } else {
      return makeRequest(startQuery, baseParams || null);
    }
  };

  search =
    <TResponse = any>(auth: ServiceAuth) =>
    async (
      project: string,
      query: any,
      paginationHandler?: PaginationHandler,
      params?: any,
      cohortId?: string,
    ) => {
      return this._paginatedRequest<TResponse>(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        paginationHandler,
        query,
        async (query, queryParams) =>
          this.getFhirSearchAPI(auth).post<TResponse>(
            cohortId
              ? urls.api.fhirSearch.cohort(project, cohortId, queryParams)
              : urls.api.fhirSearch.projects(project, queryParams),
            query,
          ),
        params,
      );
    };

  searchPatient =
    <TResponse = any>(auth: ServiceAuth) =>
    async (
      project: string,
      patientId: string,
      query: any,
      paginationHandler?: PaginationHandler,
      params?: any,
    ) =>
      this._paginatedRequest<TResponse>(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        paginationHandler,
        query,
        async (query, queryParams) =>
          this.getFhirSearchAPI(auth).post<TResponse>(
            urls.api.fhirSearch.patient(project, patientId, queryParams),
            query,
          ),
        params,
      );

  searchDevice =
    <TResponse = any>(auth: ServiceAuth) =>
    async (
      project: string,
      _: string,
      query: any,
      paginationHandler?: PaginationHandler,
      params?: any,
    ) =>
      this._paginatedRequest<TResponse>(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        paginationHandler,
        query,
        async (query, queryParams) =>
          this.getFhirSearchAPI(auth).post<TResponse>(
            urls.api.fhirSearch.projects(project, queryParams),
            query,
          ),
        params,
      );

  searchCohort =
    <TResponse = any>(auth: ServiceAuth) =>
    async (
      project: string,
      cohortId: string,
      query: any,
      paginationHandler?: PaginationHandler,
      params?: any,
    ) =>
      this._paginatedRequest<TResponse>(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        paginationHandler,
        query,
        async (query, queryParams) =>
          this.getFhirSearchAPI(auth).post<TResponse>(
            urls.api.fhirSearch.cohort(project, cohortId, queryParams),
            query,
          ),
        params,
      );
}
